// -----------------------------------------------------------------------------
// SocialPulse — Central SEO / branding config.
//
// A single source of truth for:
//   • Global brand vision + mission
//   • Per-section taglines (H1/H2 sub-copy shown to visitors)
//   • Per-section meta title / description / OG copy (SEO)
//
// Consumed by <SeoHead section="..." /> and <SectionTagline section="..." />.
// Add a new section by dropping one entry into the SECTIONS map below.
// -----------------------------------------------------------------------------

export const BRAND = {
  name: "SocialPulse",
  domain: "socialpulse.com",
  // The four permitted SEO taglines the user approved — different
  // sections pick whichever tone fits (see SECTIONS.<section>.hero_line).
  taglines: {
    primary: "SocialPulse — Discover, Connect, Preserve & Share the World's Communities",
    connect: "SocialPulse — Connecting People, Places, Families & Communities Worldwide",
    family:  "SocialPulse — The Global AI Community & Family Discovery Platform",
    memorial:"SocialPulse — Explore Communities. Preserve Memories. Build Connections.",
    unify:   "SocialPulse — Where Communities, Families and Knowledge Come Together",
  },
  vision:  "One World. One Community. Millions of Stories.",
  mission:
    "To create the world's most comprehensive platform for discovering, " +
    "preserving, and connecting people, places, families, businesses, " +
    "history, knowledge, and communities — one story at a time.",
  // Home page H1 / H2 (rendered in the hero block).
  home_h1: "Discover, Connect & Preserve the World's Communities",
  home_h2: "Explore people, places, family history, memorials, businesses, food, events and local knowledge — all powered by AI.",
};

// -----------------------------------------------------------------------------
// Section taglines (short, memorable) + meta pack.
//
// Fields:
//   tagline        — one-liner shown under the section H1
//   meta_title     — 50-60 char page <title>
//   meta_desc      — 150-160 char meta description
//   og_desc        — social sharing description (~180 chars)
//   hero_line      — optional BRAND.taglines key to reuse a global tagline
// -----------------------------------------------------------------------------

export const SECTIONS = {
  home:            { tagline: "Connecting People, Places & Communities Worldwide." },
  "food-pulse":    {
    tagline: "Save food. Save money. Strengthen communities.",
    meta_title: "FoodPulse — Fresh food. Better prices. Less waste.",
    meta_desc:
      "Discover daily deals from local farms, supermarkets and food businesses, " +
      "scan labels, cut food waste, and share surplus with your community — all on FoodPulse.",
    og_desc:
      "FoodPulse connects growers, food businesses, cooks, charities and neighbors " +
      "to discover food, share surplus, reduce waste, and improve access to nutritious food.",
  },
  weather:         { tagline: "Stay Ahead of Every Forecast, Anywhere in the World." },
  nature:          { tagline: "Discover the Wonders of Our Natural World." },
  environment:     { tagline: "Protecting Nature for Future Generations." },
  climate:         { tagline: "Understanding Climate. Preparing for Tomorrow." },
  health:          { tagline: "Empowering Healthier Lives Through Trusted Information." },
  food:            { tagline: "Explore, Cook, Share & Celebrate Food Around the World." },
  recipes:         { tagline: "Thousands of Recipes, One Global Kitchen." },
  travel:          { tagline: "Explore the World, One Destination at a Time." },
  tourism:         { tagline: "Experience Cultures, Places & Adventures Worldwide." },
  news:            { tagline: "Local Stories. Global Perspectives." },
  sports:          { tagline: "Every Sport. Every Team. Every Fan." },
  business:        { tagline: "Connecting Businesses with Their Communities." },
  education:       { tagline: "Learning Without Borders." },
  science:         { tagline: "Discoveries That Shape Our Future." },
  technology:      { tagline: "Innovation That Connects the World." },
  ai:              { tagline: "Exploring the Future of Artificial Intelligence." },
  history:         { tagline: "Preserving the Stories That Shaped Humanity." },
  genealogy:       { tagline: "Discover Your Roots. Preserve Your Legacy." },
  family_tree:     {
    tagline: "Every Family Has a Story Worth Preserving.",
    hero_line: "family",
    meta_title: "Family Tree | SocialPulse — Global Genealogy Platform",
    meta_desc:
      "Build your family tree, connect ancestors and descendants, preserve " +
      "photos, stories, and memorials, and share your family's living history " +
      "with the world on SocialPulse.",
  },
  obituaries:      {
    tagline: "Honoring Lives. Preserving Memories Forever.",
    hero_line: "memorial",
    meta_title: "Obituaries & Memorials | SocialPulse",
    meta_desc:
      "Discover recent obituaries, create lasting memorials, connect loved " +
      "ones to your family tree, and preserve their story with tributes, " +
      "photos and messages from around the world.",
  },
  memorials:       { tagline: "Honoring Lives. Preserving Memories Forever.", hero_line: "memorial" },
  community:       { tagline: "Building Stronger Communities Together." },
  events:          { tagline: "Discover What's Happening Around You." },
  local_directory: { tagline: "Find the Best of Every Community." },
  businesses:      { tagline: "Discover Trusted Businesses Near You." },
  restaurants:     { tagline: "Find Great Food Wherever You Go." },
  hotels:          { tagline: "Stay Comfortably, Wherever Life Takes You." },
  faith:           { tagline: "Connecting Faith, Hope & Community." },
  volunteer:       { tagline: "Make a Difference in Your Community." },
  nonprofits:      { tagline: "Supporting Organizations That Change Lives." },
  charities:       { tagline: "Connecting Compassion with Action." },
  food_assistance: { tagline: "Helping Communities Access Nutritious Food." },
  farmers_markets: { tagline: "Fresh, Local & Community Grown." },
  agriculture:     { tagline: "Growing the Future Together." },
  gardening:       { tagline: "Cultivating Beautiful Gardens & Healthy Harvests." },
  wildlife:        { tagline: "Protecting Earth's Incredible Biodiversity." },
  parks_recreation:{ tagline: "Explore Nature, Adventure & Outdoor Living." },
  maps:            { tagline: "Navigate the World with Confidence." },
  photography:     { tagline: "Capturing the Beauty of Every Moment." },
  art_culture:     { tagline: "Celebrating Creativity Across the Globe." },
  arts:            { tagline: "Celebrating Creativity Across the Globe." },
  music:           { tagline: "The Soundtrack of Every Community." },
  books:           { tagline: "Stories That Inspire Generations." },
  movies:          { tagline: "Discover, Watch & Share Great Entertainment." },
  shopping:        { tagline: "Find Great Products from Around the World." },
  real_estate:     { tagline: "Helping You Find the Perfect Place to Call Home." },
  transportation:  { tagline: "Keeping Communities Connected." },
  public_safety:   { tagline: "Safer Communities Start with Awareness." },
  civic_reports:   { tagline: "Report Issues. Inspire Action. Improve Communities." },
  infrastructure:  { tagline: "Building Better Communities Together." },
  emergency_alerts:{ tagline: "Stay Informed. Stay Safe." },
  jobs:            { tagline: "Connecting Talent with Opportunity." },
  government:      { tagline: "Connecting Citizens with Public Services." },
  elections:       { tagline: "Informed Citizens. Stronger Democracies." },
  pets:            { tagline: "Celebrating Our Animal Companions." },
  memorial_gardens:{ tagline: "Places of Peace, Reflection & Remembrance." },
  forums:          { tagline: "Share Ideas. Ask Questions. Build Community." },
  videos:          { tagline: "Watch, Learn & Explore the World." },
  podcasts:        { tagline: "Listen to Stories That Matter." },
  marketplace:     { tagline: "Buy, Sell & Connect Locally." },
  classifieds:     { tagline: "Connecting Buyers and Sellers in Every Community." },
  housing:         { tagline: "Helping You Find the Perfect Place to Call Home." },
};

// Convenience — a resolver that always returns *something* even if the
// caller passes an unknown section key.
export function getSection(key) {
  return SECTIONS[key] || { tagline: BRAND.taglines.connect };
}

// -----------------------------------------------------------------------------
// IndianFamilyTree.com — child-site branding + per-page SEO pack. Consumed
// by <SeoHead site="indianfamilytree" section="..." /> and by the IFT
// landing/blueprint pages. Copy is drawn verbatim from the founder's
// approved SEO blueprint so keywords + taglines line up with our
// paid-search + organic strategy.
// -----------------------------------------------------------------------------
export const IFT_BRAND = {
  name: "IndianFamilyTree.com",
  legal_name: "IndianFamilyTree.com",
  domain: "indianfamilytree.com",
  url: "https://www.indianfamilytree.com",
  tagline_primary:
    "Discover Your Roots. Preserve Your Legacy. Connect Generations.",
  tagline_alt:
    "India's Digital Home for Family History, Memories & Heritage.",
  mission:
    "To preserve India's diverse family histories, stories, traditions, " +
    "photographs, and ancestral connections for future generations " +
    "through innovative genealogy technology.",
  brand_message:
    "IndianFamilyTree.com is not just a genealogy website. It is a living " +
    "digital archive of India's families, traditions, memories, and stories " +
    "— connecting the past, present, and future.",
  same_as: [
    // Add real social profiles here as they go live.
    "https://www.facebook.com/indianfamilytree",
    "https://twitter.com/indianfamilytree",
    "https://www.instagram.com/indianfamilytree",
  ],
  logo: "https://www.indianfamilytree.com/assets/ift-logo.png",
};

// Per-page SEO pack for IFT — title / description / keywords / H1 / H2
// pulled directly from the blueprint.  Keys correspond to the URL slug
// (with '/' → 'home').  <SeoHead site="indianfamilytree" section="..."/>
// resolves through this map first, falling back to SECTIONS.
export const IFT_SECTIONS = {
  home: {
    path: "/",
    meta_title:
      "Indian Family Tree | Discover Your Roots & Preserve Your Family Legacy",
    meta_desc:
      "Build your Indian family tree, discover ancestors, preserve family " +
      "memories, create memorials, and connect generations worldwide with " +
      "IndianFamilyTree.com.",
    keywords: [
      "Indian family tree", "Indian genealogy", "family history India",
      "ancestry India", "Indian ancestors", "family tree maker",
      "Indian surname history", "family heritage", "genealogy website India",
    ],
    h1: "Discover Your Roots. Preserve Your Legacy. Connect Generations.",
    h2:
      "Create your digital family tree, preserve ancestral stories, honor " +
      "loved ones, and connect your family history across generations.",
  },
  my_family_tree: {
    path: "/my-family-tree",
    meta_title:
      "Create Your Family Tree Online | Indian Genealogy Platform",
    meta_desc:
      "Build your family tree online. Connect ancestors, relatives, " +
      "photographs, stories, and historical records in one secure family archive.",
    keywords: [
      "create family tree", "online family tree", "genealogy software",
      "family tree maker India", "ancestry chart", "family history software",
    ],
    h1: "Build Your Family Tree",
    h2: "Add ancestors, connect relatives, and preserve every generation.",
  },
  discover_your_roots: {
    path: "/discover-your-roots",
    meta_title: "Discover Indian Ancestry | Explore Your Family Roots",
    meta_desc:
      "Discover your native village, ancestral surname, family occupation, " +
      "migration history, and cultural heritage with IndianFamilyTree.com.",
    keywords: [
      "Indian ancestry", "ancestral roots India", "family origin",
      "Indian genealogy research", "surname history",
    ],
    h1: "Discover Your Roots",
    h2: "Trace your native village, surname, occupation and migration story.",
  },
  memorials: {
    path: "/memorials",
    meta_title:
      "Indian Memorials & Obituaries | Preserve Family Memories Forever",
    meta_desc:
      "Create digital memorials and obituaries to honor loved ones, share " +
      "life stories, photos, and memories with family worldwide.",
    keywords: [
      "Indian obituary", "memorial website", "death announcement India",
      "online memorial", "family remembrance", "tribute page",
    ],
    h1: "Every Life Has a Story Worth Remembering",
    h2:
      "Create digital memorials, obituaries, and living tributes that " +
      "families can visit for generations.",
  },
  family_stories: {
    path: "/family-stories",
    meta_title:
      "Share Indian Family Stories | Preserve Generational Memories",
    meta_desc:
      "Share Indian family stories — childhood memories, migration journeys, " +
      "achievements, celebrations, recipes and experiences — with the world.",
    keywords: [
      "family stories", "Indian traditions", "family memories",
      "oral history", "heritage stories",
    ],
    h1: "Share Your Family Stories",
    h2: "Every recipe, migration and memory adds to your family's living archive.",
  },
  family_archives: {
    path: "/family-archives",
    meta_title:
      "Digital Family Archives | Preserve Photos, Documents & Memories",
    meta_desc:
      "Store family photographs, certificates, letters, newspaper articles, " +
      "and historical records in your private digital family archive.",
    keywords: [
      "family archive", "digital photo archive", "historical documents",
      "family records",
    ],
    h1: "Your Family's Digital Archive",
    h2:
      "Photos, certificates, letters, land records and school papers — " +
      "kept safe for the next hundred years.",
  },
  genealogy_resources: {
    path: "/genealogy-resources",
    meta_title:
      "Indian Genealogy Resources | Research Your Family History",
    meta_desc:
      "Free Indian genealogy resources — government records, religious records, " +
      "census information, migration records and historical archives.",
    keywords: [
      "Indian genealogy records", "family research", "ancestry resources",
      "historical records India",
    ],
    h1: "Indian Genealogy Resources",
    h2:
      "Directory of official, religious and community records to help you " +
      "trace your ancestors.",
  },
  surnames: {
    path: "/surnames",
    meta_title: "Indian Surname History & Family Origins",
    meta_desc:
      "Explore the meaning, region, language and history behind Indian " +
      "surnames — from Iyer and Nair to Reddy, Gupta, Khan, Singh and more.",
    keywords: [
      "Indian surnames", "surname meaning", "family name origin",
      "Indian communities",
    ],
    h1: "Indian Surnames & Communities",
    h2: "Every surname carries a story of language, region and history.",
  },
  ai_family_assistant: {
    path: "/ai-family-assistant",
    meta_title:
      "AI Family History Assistant | Discover Your Ancestry Faster",
    meta_desc:
      "Let AI help you write biographies, organize records, translate documents, " +
      "restore old photos and build a beautiful family history timeline.",
    keywords: [
      "AI genealogy", "family history AI", "ancestry assistant",
      "genealogy research AI",
    ],
    h1: "Your AI Family History Assistant",
    h2:
      "Biographies, translations, photo restoration and timelines — " +
      "generated in minutes, reviewed by you.",
  },
  pricing: {
    path: "/pricing",
    meta_title: "IndianFamilyTree Pricing | Preserve Your Family Legacy",
    meta_desc:
      "Choose a plan that fits your family — free basic trees, richer Family " +
      "plans with unlimited members, or Heritage plans with premium memorials " +
      "and advanced AI tools.",
    keywords: [
      "family tree pricing", "genealogy subscription", "premium memorial",
      "family archive plan",
    ],
    h1: "Plans for Every Family",
    h2: "Free to start. Upgrade when your archive outgrows the basics.",
  },
  about: {
    path: "/about",
    meta_title:
      "About IndianFamilyTree.com | Preserving Indian Family Heritage",
    meta_desc:
      "Our mission is to preserve India's diverse family histories, stories, " +
      "traditions and ancestral connections through innovative genealogy technology.",
    keywords: [
      "about IndianFamilyTree", "Indian genealogy company",
      "family heritage platform India",
    ],
    h1: "Preserving India's Family Heritage",
    h2: "A living digital archive of families, traditions and stories.",
  },
};

// Convenience — resolve the IFT SEO block for a URL/path. Falls back to
// the `home` block so an unknown page still ships a decent title.
export function getIftSection(pathOrKey) {
  if (!pathOrKey) return IFT_SECTIONS.home;
  const key = String(pathOrKey).replace(/^\//, "").replace(/[-/]/g, "_") || "home";
  return IFT_SECTIONS[key] || IFT_SECTIONS.home;
}
